import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

// MATERIAL UI COMPONENTS
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Alert from "@mui/material/Alert";
import Stack from "@mui/material/Stack";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import Slide from "@mui/material/Slide";
import Typography from "@mui/material/Typography";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Tooltip from "@mui/material/Tooltip";
import Paper from "@mui/material/Paper";
import Divider from "@mui/material/Divider";

// ICONS
import UpdateIcon from "@mui/icons-material/Update";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import CloseIcon from "@mui/icons-material/Close";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import TapasIcon from "@mui/icons-material/Tapas";
import MeetingRoomIcon from "@mui/icons-material/MeetingRoom";
import ViewListIcon from "@mui/icons-material/ViewList";
import GridViewIcon from "@mui/icons-material/GridView";
import { blue, green, grey, orange, red } from "@mui/material/colors";

// CUSTOM COMPONENTS
import EnhancedSaloonTable from "./EnhancedSaloonTable";
import {
  fetchSaloonTables,
  setStatus,
} from "../../../store/actions/saloonTablesSlice";
import ConfirmModal from "../../modals/confirmModal";
import {
  finishSaloonOrderThunk,
  setShowOrderInfosModal,
} from "../../../store/actions/ordersSlice";
import { createTable } from "../../../api/table";
import { ERROR_MESSAGE } from "../../../common/helpersTxt";

import { alpha, Container, useTheme } from "@mui/material";
import Chip from "@mui/material/Chip";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import Button from "@mui/material/Button";

const SummaryStat = ({ label, value, color }) => (
  <Box sx={{ display: "flex", alignItems: "center", mr: 4 }}>
    <Box
      sx={{
        width: 10,
        height: 10,
        borderRadius: "50%",
        bgcolor: color,
        mr: 1.5,
      }}
    />
    <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
      {label}:
    </Typography>
    <Typography variant="subtitle1" fontWeight={700}>
      {value}
    </Typography>
  </Box>
);

const BottomActionBar = ({ children }) => {
  const theme = useTheme();
  return (
    <Slide direction="up" in={true} mountOnEnter unmountOnExit>
      <Paper
        elevation={4}
        sx={{
          position: "fixed",
          bottom: 24,
          left: "50%",
          transform: "translateX(-50%)",
          borderRadius: 50,
          bgcolor: alpha(theme.palette.background.paper, 0.9),
          backdropFilter: "blur(12px)",
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          px: 1,
          py: 0.75,
          zIndex: 1000,
          display: "flex",
          gap: 1,
        }}
      >
        {children}
      </Paper>
    </Slide>
  );
};

export default function EnhancedSaloon() {
  const dispatch = useDispatch();
  const history = useHistory();
  const theme = useTheme();

  const { tables, status, error } = useSelector((state) => state.saloonTables);
  const showNewOrderModal = useSelector(
    (state) => state.orders.showNewOrderModal
  );
  const showOrderInfosModal = useSelector(
    (state) => state.orders.showOrderInfosModal
  );

  const [showConfirmNewTableModal, setShowConfirmNewTableModal] =
    useState(false);
  const [showConfirmFinishOrderModal, setShowConfirmFinishOrderModal] =
    useState(false);
  const [tableOrder, setTableOrder] = useState([]);
  const [openErrorAlert, setOpenErrorAlert] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [viewMode, setViewMode] = useState("grid");
  const [currentArea, setCurrentArea] = useState("main");

  // Restaurant areas
  const areas = [
    { id: "main", label: "Salão Principal", icon: <RestaurantIcon /> },
    { id: "bar", label: "Bar", icon: <TapasIcon /> },
    { id: "terrace", label: "Terraço", icon: <MeetingRoomIcon /> },
  ];

  // Filter tables by area
  const filteredTables = tables.filter(
    (table) => !table.area || table.area === currentArea
  );

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      dispatch(fetchSaloonTables());
    } catch (e) {
      handleError();
    }
  };

  const handleTableAction = async (table) => {
    if (table.order?.order_id) {
      setTableOrder(table.order);
      handleFinishOrderModal();
    }
  };

  const handleFinishOrderModal = () => {
    setShowConfirmFinishOrderModal(!showConfirmFinishOrderModal);
  };

  const handleFinishOrder = () => {
    handleFinishOrderModal();
    dispatch(finishSaloonOrderThunk({ order: tableOrder }));
    dispatch(setStatus("loading"));

    setTimeout(async () => {
      await loadData();
    }, 3000);
  };

  const handleNewOrder = (table) => {
    // Route to the newOrder component with table data
    history.push({
      pathname: "/pedido/novo",
      state: { tableInfo: table, orderType: "INDOOR" },
    });
  };

  const handleTableShowOrderDetails = (table) => {
    const { order_id } = table.order;
    dispatch(
      setShowOrderInfosModal({
        orderId: order_id,
        show: !showOrderInfosModal,
      })
    );
  };

  const handleNewTableModal = () => {
    setShowConfirmNewTableModal(!showConfirmNewTableModal);
  };

  const handleNewTable = async () => {
    try {
      await createTable({ area: currentArea });
      await loadData();
      handleNewTableModal();
    } catch (e) {
      handleError();
    }
  };

  const handleError = (message) => {
    setOpenErrorAlert(true);
    setErrorMessage(message ?? ERROR_MESSAGE);
  };

  const handleChangeArea = (event, newValue) => {
    setCurrentArea(newValue);
  };

  const toggleViewMode = () => {
    setViewMode(viewMode === "grid" ? "list" : "grid");
  };

  // Table statistics
  const occupiedTables = filteredTables.filter(
    (table) => table.order?.order_id
  ).length;
  const availableTables = filteredTables.length - occupiedTables;
  const occupancyRate =
    filteredTables.length > 0
      ? Math.round((occupiedTables / filteredTables.length) * 100)
      : 0;

  if (status === "loading") {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "80vh",
        }}
      >
        <CircularProgress sx={{ "&:focus-visible": { border: "none" } }} />
      </Box>
    );
  }

  return (
    <>
      <Container maxWidth="xl" sx={{ pb: 12, pt: 2 }}>
        <Box>
          {/* Header Area */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              alignItems: { xs: "flex-start", md: "center" },
              justifyContent: "space-between",
              mb: 4,
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="h4" fontWeight="800" gutterBottom>
                Mesas
              </Typography>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                flexWrap="wrap"
              >
                <SummaryStat
                  label="Total"
                  value={filteredTables.length}
                  color={grey[400]}
                />
                <SummaryStat
                  label="Livres"
                  value={availableTables}
                  color={green[400]}
                />
                <SummaryStat
                  label="Ocupadas"
                  value={occupiedTables}
                  color={blue[400]}
                />
                <SummaryStat
                  label="Ocupação"
                  value={`${occupancyRate}%`}
                  color={
                    occupancyRate > 80
                      ? red[400]
                      : occupancyRate > 50
                      ? orange[400]
                      : green[400]
                  }
                />
              </Stack>
            </Box>

            <Tabs
              value={currentArea}
              onChange={handleChangeArea}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                "& .MuiTabs-indicator": {
                  display: "none",
                },
                "& .MuiTab-root": {
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: "0.95rem",
                  borderRadius: 50,
                  mr: 1,
                  minHeight: 40,
                  px: 2.5,
                  border: "1px solid",
                  borderColor: "divider",
                  transition: "all 0.2s ease",
                  "&.Mui-selected": {
                    bgcolor: "text.primary",
                    color: "background.paper",
                    borderColor: "text.primary",
                  },
                  "&:hover:not(.Mui-selected)": {
                    bgcolor: alpha(theme.palette.text.primary, 0.05),
                    borderColor: theme.palette.text.primary,
                  },
                },
              }}
            >
              {areas.map((area) => (
                <Tab key={area.id} value={area.id} label={area.label} />
              ))}
            </Tabs>
          </Box>

          {/* Error Alert */}
          <Box sx={{ my: 1 }}>
            <Collapse in={openErrorAlert}>
              <Alert
                variant="filled"
                severity="error"
                action={
                  <IconButton
                    aria-label="close"
                    color="inherit"
                    size="small"
                    onClick={() => setOpenErrorAlert(false)}
                  >
                    <CloseIcon fontSize="inherit" />
                  </IconButton>
                }
                sx={{ mb: 2, borderRadius: 2 }}
              >
                {errorMessage}
              </Alert>
            </Collapse>
          </Box>

          {/* Tables Grid/List */}
          {viewMode === "grid" ? (
            <Grid container spacing={3}>
              {filteredTables.map((table) => (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={4}
                  lg={3}
                  xl={2.4} // 5 columns on extra large screens
                  key={table.id || table.index}
                >
                  <EnhancedSaloonTable
                    table={table}
                    action={handleTableAction}
                    newOrder={handleNewOrder}
                    openOrderDetails={handleTableShowOrderDetails}
                  />
                </Grid>
              ))}
            </Grid>
          ) : (
            <Paper
              variant="outlined"
              sx={{
                borderRadius: 3,
                overflow: "hidden",
                border: "none",
                boxShadow: "0 4px 20px 0 rgba(0,0,0,0.05)",
              }}
            >
              {filteredTables.map((table, index) => (
                <React.Fragment key={table.id || table.index}>
                  <Box
                    sx={{
                      p: 2.5,
                      bgcolor: "background.paper",
                      cursor: "pointer",
                      transition: "background-color 0.2s",
                      "&:hover": {
                        bgcolor: alpha(theme.palette.action.hover, 0.04),
                      },
                      borderLeft: "4px solid",
                      borderColor: table.order?.order_id
                        ? blue[500]
                        : green[400],
                    }}
                    onClick={() =>
                      table.order?.order_id
                        ? handleTableShowOrderDetails(table)
                        : handleNewOrder(table)
                    }
                  >
                    <Grid container alignItems="center" spacing={2}>
                      <Grid item xs={2} sm={1}>
                        <Typography variant="h6" fontWeight="bold">
                          #{table.index + 1}
                        </Typography>
                      </Grid>
                      <Grid item xs={3} sm={2}>
                        <Chip
                          label={`${table.seats || 4} Lugares`}
                          size="small"
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item xs={7} sm={4}>
                        <Typography fontWeight="600" noWrap>
                          {table.order?.order_id
                            ? table.order?.customer_name || "Cliente não id."
                            : "Disponível"}
                        </Typography>
                        {table.order?.created_at && (
                          <Typography variant="caption" color="text.secondary">
                            Início:{" "}
                            {new Date(
                              table.order?.created_at
                            ).toLocaleTimeString([], {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </Typography>
                        )}
                      </Grid>
                      <Grid
                        item
                        xs={6}
                        sm={3}
                        sx={{ textAlign: { xs: "left", sm: "right" } }}
                      >
                        {table.order?.total && (
                          <Typography
                            fontWeight="bold"
                            color="primary.main"
                            variant="h6"
                          >
                            R$ {(table.order?.total || 0).toFixed(2)}
                          </Typography>
                        )}
                      </Grid>
                      <Grid item xs={6} sm={2} sx={{ textAlign: "right" }}>
                        {table.order?.order_id ? (
                          <IconButton
                            color="success"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleTableAction(table);
                            }}
                          >
                            <CheckCircleIcon />
                          </IconButton>
                        ) : (
                          <IconButton
                            color="primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleNewOrder(table);
                            }}
                          >
                            <AddCircleIcon />
                          </IconButton>
                        )}
                      </Grid>
                    </Grid>
                  </Box>
                  {index < filteredTables.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </Paper>
          )}
        </Box>
      </Container>

      {/* Floating Action Bar */}
      <BottomActionBar>
        <Tooltip title="Alternar Visualização">
          <IconButton onClick={toggleViewMode} color="default">
            {viewMode === "grid" ? <ViewListIcon /> : <GridViewIcon />}
          </IconButton>
        </Tooltip>
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
        <Tooltip title="Atualizar">
          <IconButton onClick={loadData}>
            <UpdateIcon />
          </IconButton>
        </Tooltip>
        <Button
          variant="contained"
          startIcon={<AddCircleIcon />}
          onClick={handleNewTableModal}
          sx={{ borderRadius: 50, px: 3, fontWeight: "bold" }}
        >
          Nova Mesa
        </Button>
      </BottomActionBar>

      {/* Modals */}
      {showConfirmNewTableModal && (
        <ConfirmModal
          show={showConfirmNewTableModal}
          label="Criar Mesa"
          message={`Deseja criar uma nova mesa na área ${
            areas.find((a) => a.id === currentArea)?.label
          }?`}
          action={handleNewTable}
          closeModal={handleNewTableModal}
        />
      )}

      {showConfirmFinishOrderModal && (
        <ConfirmModal
          show={showConfirmFinishOrderModal}
          label="Fechar Mesa"
          message={`Tem certeza que deseja fechar a mesa ${
            tableOrder.customer_name || ""
          }?`}
          action={handleFinishOrder}
          closeModal={() => setShowConfirmFinishOrderModal(false)}
        />
      )}
    </>
  );
}
